import React, { cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";

interface OverflowTooltipProps {
  children: React.ReactElement;
  tooltipTitle: string;
}

const OverflowTooltip: React.FC<OverflowTooltipProps> = ({ children, tooltipTitle }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  if (!isValidElement(children)) {
    console.warn("OverflowTooltip 只支持可掛 ref 的元素");
    return children;
  }

  // 有些写法是 .multLineEllipsis(1);
  // 有些写法是  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
  const checkOverflow = () => {
    if (textRef.current) {
      const el = textRef.current;
      const computedStyle = window.getComputedStyle(el);

      const whiteSpace = computedStyle.whiteSpace;
      const lineClamp = computedStyle.getPropertyValue("-webkit-line-clamp");

      let overflow = false;

      if (lineClamp && lineClamp !== "none") {
        // 多行省略情况
        overflow = el.scrollHeight - el.clientHeight > 1;
      } else if (whiteSpace === "nowrap") {
        // 单行省略情况
        overflow = el.scrollWidth > el.clientWidth;
      } else {
        // 默认也可以按宽度判断（更保险）
        overflow = el.scrollWidth > el.clientWidth;
      }

      setIsOverflow(overflow);
    }
  };

  useEffect(() => {
    if (!textRef.current) return;

    const observer = new ResizeObserver(() => {
      checkOverflow();
    });

    observer.observe(textRef.current);

    // 初次挂载也检测一次
    checkOverflow();

    return () => {
      observer.disconnect();
    };
  }, [tooltipTitle]);


  const childWithRef = cloneElement(children as any, { ref: textRef });

  return (
    <>
      {isOverflow ? (
        <Tooltip title={tooltipTitle} overlayStyle={{ position: "fixed", top: 0 }}>
          {childWithRef}
        </Tooltip>
      ) : (
        childWithRef
      )}
    </>
  );
};

export default OverflowTooltip;
